import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Link } from '@codex/basics';
import { Icon } from '@codex/icons';

const Item = styled.li`
  border-bottom: 1px solid ${props => props.theme.colors.shades[5]};
  list-style: none;
  margin: 0;

  &:last-child {
    border-bottom: none;
  }
`;

const MenuLinkLabel = styled.span`
  font-size: 14px;
  font-weight: bold;
  line-height: 1.3;
`;

const MenuLink = styled(Link)`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  text-decoration: none;
  padding: 11px 5px;

  &${Link} {
    text-decoration: none
  }

  :hover {
    ${MenuLinkLabel} {
      text-decoration: underline;
    }
  }
`;

const MenuIconWrapper = styled.div`
  width: 40px;
`;

const MenuIcon = styled(Icon)`
  && {
    margin: 0 10px;
    width: 20px;
    height: 20px;
    stroke: none;
  }

  g {
    fill: currentColor;
  }

  &.dc-icon-my-details {
    position: relative;
    left: 2px;
    width: 22px;
  }
`;

const NotificationsLabel = styled.span`
  font-size: 12px;
  margin-left: 4px;
  margin-right: 5px;
`;

const Spacer = styled.div`
  flex: 1;
`;

const MenuLinkCount = styled.span`
  display: inline-block;
  width: ${props => (props.extended ? '21px' : '17px')};
  height: 17px;
  vertical-align: middle;
  border-radius: 17px;
  text-align: center;
  line-height: 17px;
  font-size: 13px;
  color: ${props => props.theme.colors.shades[7]};
  background-color: ${props => props.theme.colors.error.default};
`;

const MenuLinkExtra = styled.div`
  position: relative;
  color: ${props => props.theme.colors.shades[3]};
  padding: 0 5px;
`;

const MenuLinkArrow = styled(Icon)`
  && {
    width: 22px;
    height: 20px;
    margin: 0 2px 0 15px;
    align-self: flex-end;
  }
`;

const MenuItem = props => (
  <Item key={props.label} data-interaction={props.dataInteraction}>
    <MenuLink
      href={props.link}
      qa={props.qa}
      onClick={() => {
        props.onClick();
        const event = new Event('clickDropdownLink');
        event.linkPath = props.link;
        document.dispatchEvent(event);
      }}
    >
      <MenuIconWrapper>
        <MenuIcon icon={props.icon} />
      </MenuIconWrapper>
      <MenuLinkLabel>{props.label}</MenuLinkLabel>
      {props.children && props.notificationsCount > 0 && (
        <>
          <NotificationsLabel>- {props.notificationsLabel}</NotificationsLabel>
          <MenuLinkCount extended={props.notificationsCount > 9}>
            {props.notificationsCount > 9 ? '9+' : props.notificationsCount}
          </MenuLinkCount>
        </>
      )}
      <Spacer />
      <MenuLinkArrow icon="ChevronRight" />
    </MenuLink>
    {props.children && (
      <MenuLinkExtra>
        {props.children}
      </MenuLinkExtra>
    )}
  </Item>
);

MenuItem.propTypes = {
  children: PropTypes.node,
  dataInteraction: PropTypes.string,
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
  notificationsCount: PropTypes.number,
  notificationsLabel: PropTypes.string,
  onClick: PropTypes.func,
  qa: PropTypes.string
};

MenuItem.defaultProps = {
  children: undefined,
  dataInteraction: '',
  notificationsCount: 0,
  notificationsLabel: '',
  onClick: () => {},
  qa: ''
};

export default MenuItem;
